import './FailedChecks.less';
import React, { useMemo, useState } from 'react';
import { Card, Icon, Button, Empty, Modal } from 'antd';
import ImpactIndicator from '../shared/ImpactIndicator';
import CardTitleWithProgressIndicator from '../shared/CardTitleWithProgressIndicator';


type FailedChecksData = {
    testKey: string;
    title: string;
    recommendation: string;
    lastFound: string;
    relevance: number;
    impactScore: number;
    affectedUrls?: number;
    affectedClusters?: number;
    howToFix: {
        html: string;
        video: string | null;
    } | null;
    sampleUrl: string;
};

type Props = {
    failedChecks: FailedChecksData[];
    title: string;
    type: 'url' | 'cluster';
    isLoading?: boolean;
    isRunningBatchAnalysis: boolean;
    onFailedCheckButtonClicked?: (testKey: string) => void;
    totalUrlsCount?: number;
};

type HowToFixProps = {
    seoTestTitle: string;
    howToFix: {
        html: string;
        video: string | null
    } | null;
}

type FailedCheckItemProps = {
    failedCheck: FailedChecksData;
    type: 'url' | 'cluster';
    onFailedCheckButtonClicked?: (testKey: string) => void;
    totalUrlsCount: number;
}

function HowToFix({ seoTestTitle, howToFix }: HowToFixProps) {
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

    const handleToggleModal = () => {
        setIsModalVisible(!isModalVisible);
    };

    if (!howToFix) {
        return null;
    }

    return (
        <>
            <div className="how-to-fix" onClick={handleToggleModal}><Icon type="tool" /> How to fix</div>
            <Modal
                centered
                className="wide-modal"
                title={seoTestTitle}
                visible={isModalVisible}
                onCancel={handleToggleModal}
                footer={null}
            >
                <div className="modal-content no-padding">
                    {howToFix.video && (
                        <iframe
                            title="video"
                            width="100%"
                            height="320"
                            src={howToFix.video}
                            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    )}
                    <div className="default-padding how-to-fix-content">
                        <h3>How to pass this test?</h3>
                        <span dangerouslySetInnerHTML={{ __html: howToFix.html }} />
                    </div>
                </div>
                <div className="button-footer">
                    <Button onClick={handleToggleModal}>Close</Button>
                </div>
            </Modal>
        </>
    )
}

function FailedCheckItem({ failedCheck, onFailedCheckButtonClicked, totalUrlsCount, type }: FailedCheckItemProps) {
    const affectedUrlsCount = failedCheck.affectedUrls || 0;
    const affectedUrlsPercentage = totalUrlsCount ? Math.round((affectedUrlsCount / totalUrlsCount) * 100) : 0;
    const affectedUrlsTxt = affectedUrlsCount > 0 ? `${affectedUrlsCount} sample URL${affectedUrlsCount > 1 ? 's' : ''}` : '';
    const affectedClustersTxt = failedCheck.affectedClusters ? `${failedCheck.affectedClusters} Page Group${failedCheck.affectedClusters > 1 ? 's' : ''}` : '';

    let percentageTxt = "";
    if (affectedUrlsPercentage === 100) {
        percentageTxt = "(100% of all URLs)";
    } else if (affectedUrlsPercentage < 1) {
        percentageTxt = "(<1% of all URLs)";
    } else {
        percentageTxt = `(~${affectedUrlsPercentage}% of all URLs)`;
    }

    let affectedUrlsText = affectedUrlsCount > 0 ? `${affectedUrlsTxt} across ${affectedClustersTxt} ${percentageTxt}` : '';
    if (type === 'cluster') {
        affectedUrlsText = affectedUrlsCount > 0 ? `${affectedUrlsTxt} ${percentageTxt}` : '';
    }

    return (
        <div
            className="failed-check-item"
            key={failedCheck.testKey}
        >
            <ImpactIndicator severity={failedCheck.impactScore} />
            <div className="item-description">
                <span>{failedCheck.title}</span>
                <span
                    className="item-description-recommendation"
                    dangerouslySetInnerHTML={{ __html: failedCheck.recommendation }}
                />
                <HowToFix
                    seoTestTitle={failedCheck.title}
                    howToFix={failedCheck.howToFix}
                />
            </div>

            <div className="affected-urls">
                {/* <h4 >Affected URLS</h4> */}
                <span className="link stats" onClick={onFailedCheckButtonClicked && (() => onFailedCheckButtonClicked(failedCheck.testKey))}>{affectedUrlsText}</span>
                <h4 >Sample URL</h4>
                <div className="sample-url truncate">
                    <span className="link" onClick={onFailedCheckButtonClicked && (() => onFailedCheckButtonClicked(failedCheck.testKey))}>{failedCheck.sampleUrl}</span>
                </div>
            </div>
        </div >
    )
}

export default function PageLevelChecks({ failedChecks, title, type, isLoading, isRunningBatchAnalysis, onFailedCheckButtonClicked, totalUrlsCount }: Props) {
    const sortedChecks = useMemo(() => [...failedChecks].sort((a, b) => b.impactScore - a.impactScore), [failedChecks]);

    return (
        <Card
            className="deep-analysis-failed-checks"
            loading={isLoading}
            title={<CardTitleWithProgressIndicator title={title} description="SEO audit in progress" isInProgress={isRunningBatchAnalysis} />}
        >
            <div className="failed-checks-list">
                {sortedChecks.length === 0 && <Empty description={isRunningBatchAnalysis ? 'No failed checks yet' : 'No failed checks'} />}
                {sortedChecks.length > 0 &&
                    sortedChecks.map((failedCheck) => (
                        <FailedCheckItem
                            key={failedCheck.testKey}
                            failedCheck={failedCheck}
                            type={type}
                            onFailedCheckButtonClicked={onFailedCheckButtonClicked}
                            totalUrlsCount={totalUrlsCount || 0}
                        />
                    ))}
            </div>
        </Card>
    );
}
